<template>
    <loading-screen
      v-if="showLoading"
      :visible="showLoading"
      :duration="1000"
      @complete="handleLoadingComplete"
    />
    <transition 
      name="page-entrance" 
      @enter="handlePageEnter"
      @after-enter="handlePageEnterComplete"
    >
      <child 
        v-if="!showLoading"
        :class="{ 'page-entering': isEntering }"
      />
    </transition>
</template>

<script>
import { ref, onMounted, nextTick, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import LoadingScreen from '~/components/LoadingScreen.vue'
import Child from '~/components/Child.vue'
import { useLoadingOrchestrator } from '~/services/LoadingOrchestrator.js'

export default {
  name: 'App',

  components: {
    LoadingScreen,
    Child
  },

  setup() {
    const route = useRoute()
    const showLoading = ref(true)
    const isEntering = ref(false)
    const pageFullyLoaded = ref(false)

    // Check if current route should skip global loading
    const shouldSkipLoading = computed(() => {
      return route.meta?.skipGlobalLoading === true
    })

    const {
      loadingOrchestrator,
      loadingProgress,
      criticalResourcesLoaded,
      canStartPageEntrance,
      isFullyReady
    } = useLoadingOrchestrator()

    const handleLoadingComplete = () => {
      // Mark loading phase as complete
      loadingOrchestrator.markLoadingComplete()

      // Immediate handoff for seamless transition (loading screen already has fade-out)
      if (canStartPageEntrance.value) {
        isEntering.value = true
        showLoading.value = false
      }
    }

    const handlePageEnter = (el) => {
      // Page entrance animation started
      el.style.opacity = '0'
      el.style.transform = 'translateY(20px)'

      // Trigger entrance animation
      nextTick(() => {
        el.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
      })
    }

    const handlePageEnterComplete = () => {
      isEntering.value = false
      pageFullyLoaded.value = true

      // Notify any listeners that page entrance is complete
      window.dispatchEvent(new CustomEvent('page-entrance-complete', {
        detail: { fullyReady: isFullyReady.value }
      }))
    }

    // Watch for route changes to handle skipGlobalLoading dynamically
    // Handles BOTH directions: TO and FROM skipGlobalLoading routes
    watch(() => route.meta?.skipGlobalLoading, (skipLoading, oldSkipLoading) => {
      // Case 1: Navigating TO a skipGlobalLoading route (e.g., welcome -> campaign-countdown)
      if (skipLoading === true) {
        // Show loading screen first to prevent blank page during lazy import
        showLoading.value = true

        // Wait for lazy-loaded component to finish importing
        setTimeout(() => {
          showLoading.value = false
        }, 800)
      }
      // Case 2: Navigating FROM skipGlobalLoading route to normal route (e.g., campaign-ended -> welcome)
      else if (oldSkipLoading === true && !skipLoading) {
        // Show loading screen during layout transition
        showLoading.value = true

        // Wait for layout change and component mounting
        setTimeout(() => {
          showLoading.value = false
        }, 800)
      }
    })

    onMounted(() => {
      // Scrolls to top when view is displayed
      window.scrollTo(0, 0)

      // Skip global loading if route specifies it (checked by watcher now)
      if (shouldSkipLoading.value) {
        setTimeout(() => {
          showLoading.value = false
        }, 400) // Small delay for smooth transition
        return
      }

      // Initialize loading orchestrator
      loadingOrchestrator.init().catch(error => {
        console.warn('Loading orchestrator initialization failed:', error)
      })

      // Setup progress monitoring
      loadingOrchestrator.onProgress((progress) => {
        console.log(`Asset loading progress: ${Math.round(progress)}%`)
      })

      loadingOrchestrator.onCriticalLoaded(() => {
        console.log('Critical assets loaded - loading screen can proceed')
      })

      loadingOrchestrator.onAllLoaded(() => {
        console.log('All assets loaded - ready for optimal experience')
      })

      loadingOrchestrator.onReadyForEntrance(() => {
        console.log('Page entrance ready - smooth handoff initiated')
      })
    })

    return {
      showLoading,
      isEntering,
      pageFullyLoaded,
      loadingProgress,
      criticalResourcesLoaded,
      shouldSkipLoading,
      handleLoadingComplete,
      handlePageEnter,
      handlePageEnterComplete
    }
  }
}
</script>

<style lang="scss" scoped>
// Page entrance transition
.page-entrance-enter-active {
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.page-entrance-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-entrance-enter-to {
  opacity: 1;
  transform: translateY(0);
}

// Page entering state for staggered animations
.page-entering {
  .wizard-step,
  .wizard-content,
  .step-navigation {
    opacity: 0;
    transform: translateY(10px);
    animation: staggeredEntrance 0.6s ease-out forwards;
  }
  
  .wizard-step:nth-child(1) { animation-delay: 0.1s; }
  .wizard-step:nth-child(2) { animation-delay: 0.2s; }
  .wizard-step:nth-child(3) { animation-delay: 0.3s; }
  .wizard-content { animation-delay: 0.15s; }
  .step-navigation { animation-delay: 0.4s; }
}

@keyframes staggeredEntrance {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// Ensure smooth performance
* {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  -ms-backface-visibility: hidden;
}
</style>
